import React, { useState } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import {
  Paper, TextField, Button, Typography, InputAdornment, IconButton, makeStyles, CircularProgress
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
    backgroundImage: theme.palette.type === 'dark'
      ? 'radial-gradient(circle at 50% -20%, rgba(16,185,129,0.15) 0%, transparent 50%), radial-gradient(circle at 10% 100%, rgba(212,168,83,0.1) 0%, transparent 40%)'
      : 'radial-gradient(circle at 50% -20%, rgba(16,185,129,0.06) 0%, transparent 50%), radial-gradient(circle at 10% 100%, rgba(212,168,83,0.05) 0%, transparent 40%)',
    position: 'relative',
    overflow: 'hidden' },
  glowBg: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    height: 600,
    background: 'radial-gradient(circle, rgba(16,185,129,0.05) 0%, transparent 70%)',
    zIndex: 0,
    pointerEvents: 'none' },
  themeToggle: {
    position: 'absolute',
    top: 24,
    right: 24,
    zIndex: 2,
    width: 40,
    height: 40,
    borderRadius: 10,
    background: theme.palette.type === 'dark'
      ? 'rgba(148,163,184,0.06)'
      : 'rgba(45,42,36,0.04)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.12)'
      : '1px solid rgba(45,42,36,0.08)',
    color: theme.palette.text.secondary,
    '&:hover': {
      background: 'rgba(16,185,129,0.1)',
      borderColor: 'rgba(16,185,129,0.3)',
      color: '#10b981' } },
  paper: {
    padding: theme.spacing(5, 6),
    maxWidth: 440,
    width: '100%',
    borderRadius: 24,
    background: theme.palette.type === 'dark'
      ? 'rgba(30,41,59,0.7)'
      : 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.1)'
      : '1px solid rgba(45,42,36,0.06)',
    boxShadow: theme.palette.type === 'dark'
      ? '0 25px 50px -12px rgba(0,0,0,0.5)'
      : '0 25px 50px -12px rgba(45,42,36,0.08)',
    zIndex: 1 },
  logo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(4) },
  logoText: {
    fontWeight: 800,
    fontSize: '2rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: 1.1 },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: 8,
    fontWeight: 500,
    fontSize: '0.95rem' },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20 },
  submitBtn: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.8),
    fontSize: '1rem',
    borderRadius: 12,
    letterSpacing: '0.02em' },
  error: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.2)',
    padding: '12px 16px',
    borderRadius: 10,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8 },
  errorText: {
    color: '#f87171',
    fontSize: '0.85rem',
    fontWeight: 500 },
  inputIcon: {
    color: theme.palette.text.disabled,
    marginRight: 8 },
  inputField: {
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(30,41,59,0.7) !important'
        : 'rgba(255,255,255,0.8) !important',
      borderRadius: 12,
      color: theme.palette.text.primary },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.type === 'dark'
        ? 'rgba(148,163,184,0.15)'
        : 'rgba(45,42,36,0.12)',
      borderRadius: 12 },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.type === 'dark'
        ? 'rgba(148,163,184,0.3)'
        : 'rgba(16,185,129,0.4)' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
      borderWidth: 1 },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
      '&::placeholder': {
        color: theme.palette.text.secondary,
        opacity: 1 },
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
        transition: 'background-color 5000s ease-in-out 0s, box-shadow 5000s ease-in-out 0s, -webkit-box-shadow 5000s ease-in-out 0s',
        WebkitTextFillColor: `${theme.palette.text.primary} !important`,
        caretColor: theme.palette.text.primary,
        border: 'none !important',
        outline: 'none !important' } } } }));

export default function LoginPage() {
  const classes = useStyles();
  
  const history = useHistory();
  const { login, isLoggedIn, loading, error } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  if (isLoggedIn) return <Redirect to="/dashboard" />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await login(email, password);
    if (success) history.push('/dashboard');
  };

  return (
    <div className={classes.root}>
      <IconButton className={classes.themeToggle} onClick={toggleTheme} size="small">
        {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
      </IconButton>
      <div className={classes.glowBg} />
      <Paper className={classes.paper} elevation={0}>
        <div className={classes.logo}>
          <div style={{ marginBottom: 20 }}>
            <img src="/logo.png" alt="Noor Admin Logo" style={{ width: 80, height: 80, borderRadius: 20, boxShadow: '0 8px 24px rgba(16,185,129,0.2)' }} />
          </div>
          <Typography className={classes.logoText}>Noor Admin</Typography>
          <Typography className={classes.subtitle}>Secure portal access</Typography>
        </div>

        {error && (
          <div className={classes.error}>
            <Typography className={classes.errorText}>{error}</Typography>
          </div>
        )}

        <form onSubmit={handleSubmit} className={classes.form}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            className={classes.inputField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon className={classes.inputIcon} fontSize="small" />
                </InputAdornment>
              ) }}
          />
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={classes.inputField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon className={classes.inputIcon} fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ) }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submitBtn}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} style={{ color: '#FFFFFF' }} /> : 'Authenticate'}
          </Button>
        </form>
      </Paper>
    </div>
  );
}
